/*
 * Copyright 2025 coze-dev Authors
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { useNavigate } from 'react-router-dom';
import { type ReactNode, type FC, useEffect } from 'react';

import { useShallow } from 'zustand/react/shallow';
import classNames from 'classnames';
import { useSpaceStore } from '@coze-foundation/space-store';
import { localStorageService } from '@coze-foundation/local-storage';
import { EVENT_NAMES, sendTeaEvent } from '@coze-arch/bot-tea';
import { useErrorHandler, reporter } from '@coze-arch/logger';
import { CustomError } from '@coze-arch/bot-error';
import { REPORT_EVENTS as ReportEventNames } from '@coze-arch/report-events';
import { I18n } from '@coze-arch/i18n';
import { rokidAccountBaseUrl } from '@rokid/host';
// rokid 登录认证页跳转
import {
  redirectToRokidAccountCenter,
  handleDeveloperVerify,
} from '@rokid/account';

export interface IWorkspaceListItem {
  icon?: ReactNode;
  activeIcon?: ReactNode;
  title?: () => string;
  path?: string;
  dataTestId?: string;
  module?: string;
}

interface IWorkspaceListItemProps extends IWorkspaceListItem {
  currentSubMenu?: string;
}

export const WorkspaceListItem: FC<IWorkspaceListItemProps> = ({
  icon,
  activeIcon,
  title,
  path,
  currentSubMenu,
  dataTestId,
  module,
}) => {
  const navigate = useNavigate();
  const capture = useErrorHandler();
  const { spaceId, fetchSpaces, spaceList, setSpace } = useSpaceStore(
    useShallow(store => ({
      spaceId: store.space.id,
      fetchSpaces: store.fetchSpaces,
      spaceList: store.spaceList,
      setSpace: store.setSpace,
    })),
  );
  //rokid 二级菜单提级以后，通过这种方式强制查询spaces列表
  useEffect(() => {
    if (spaceList.length) {
      setSpace(spaceList[0].id);
    } else {
      if (!spaceId) {
        fetchSpaces(true);
      }
    }
  }, [spaceId, spaceList]);
  return (
    <div
      onClick={async () => {
        if (module === 'workspace') {
          sendTeaEvent(EVENT_NAMES.coze_space_sidenavi_ck, {
            item: title?.() || 'unknown-workspace-submenu',
            navi_type: 'second',
            need_login: true,
            have_access: true,
          });
          localStorageService.setValue('workspace-subMenu', path);
          // Special handling for home menu item
          if (path === 'HOME') {
            navigate('/space/home');
          } else {
            if (spaceId) {
              navigate(`/space/${spaceId}/${path}`);
            } else {
              try {
                await fetchSpaces(true);
              } catch (err) {
                const status = (err as Error & { status: number }).status;
                // rokid 403 代表无开发者认证，提示跳转至开发者认证页面
                if (status === 403) {
                  const { subTitleKey, guideUrl } = handleDeveloperVerify(
                    err?.response?.data?.msg,
                  );
                  capture(
                    new CustomError(ReportEventNames.errorPath, 'no verify', {
                      customGlobalErrorConfig: {
                        title: I18n.t('workspace_no_permission_access'),
                        subtitle: I18n.t(subTitleKey),
                        // rokid 403 代表无开发者认证，提示跳转至开发者认证页面
                        verify: true,
                        guideUrl,
                      },
                    }),
                  );
                } else {
                  redirectToRokidAccountCenter(window.location.href);
                }
              }
            }
          }
        } else {
          navigate(path || '/');
        }
      }}
      className={classNames(
        'flex items-center gap-[8px]',
        'transition-colors',
        'rounded-[8px]',
        'h-[32px] w-full',
        'px-[8px]',
        'cursor-pointer',
        'group',
        'hover:coz-mg-secondary-hovered',
        {
          'coz-mg-primary': path === currentSubMenu,
          'coz-fg-plus': path === currentSubMenu,
          'coz-fg-primary': path !== currentSubMenu,
        },
      )}
      id={`workspace-submenu-${path}`}
      data-testid={dataTestId}
    >
      <div className="text-[14px]">
        <div className="w-[16px] h-[16px]">
          {path === currentSubMenu ? activeIcon : icon}
        </div>
      </div>
      <div
        className={classNames(
          'flex-1',
          'text-[14px]',
          'leading-[20px]',
          'font-[500]',
        )}
      >
        {title?.()}
      </div>
    </div>
  );
};
